import {createAsyncThunk} from '@reduxjs/toolkit';
import {CLDispatch, RootState} from 'common/src/redux/store';
import {AssetFolderEntityType} from '../../../Assets.enums';
import {Folder} from '../../../Assets.types';
import {AssetFilterState} from '../../../utils/filterAssets';
import {loadAssetTreeUtil} from '../assetDisplayThunkUtils';

/**
 * Parameters for loadAssetTree thunk
 */
export interface LoadAssetTreeParams {
  entityType: AssetFolderEntityType;
  selectEntityId?: (state: RootState) => number | undefined;
  depth: number;
  treeFilters?: AssetFilterState;
  setAssetTree: (tree: Folder | null) => {type: string; payload: Folder | null};
  setCurrentFolderId: (folderId: number | null) => {type: string; payload: number | null};
  setIsLoadingTree: (isLoading: boolean) => {type: string; payload: boolean};
}

/**
 * Unified thunk to load asset tree
 * Used by all asset hooks with their slice-specific configuration
 */
export const loadAssetTree = createAsyncThunk<
  void,
  LoadAssetTreeParams,
  {dispatch: CLDispatch; state: RootState}
>(
  'loadAssetTree',
  async (
    {
      entityType,
      selectEntityId,
      depth,
      treeFilters,
      setAssetTree,
      setCurrentFolderId,
      setIsLoadingTree,
    },
    {dispatch, getState, rejectWithValue},
  ) => {
    try {
      const state = getState();
      const entityId = selectEntityId?.(state);

      // Some entities require entityId (template, creative, project)
      if (selectEntityId && !entityId) {
        throw new Error('Entity ID not found. Please load entity first.');
      }

      dispatch(setIsLoadingTree(true));

      const tree = await loadAssetTreeUtil(entityType, entityId, depth, treeFilters);

      dispatch(setAssetTree(tree));
      // folderPathIds is derived from currentFolderId in CLAssetDisplay
      dispatch(setCurrentFolderId(tree.folderId));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load asset tree';

      return rejectWithValue(errorMessage);
    } finally {
      dispatch(setIsLoadingTree(false));
    }
  },
);
